import LockOutlinedIcon from "@mui/icons-material/LockOutlined"
import { Alert, Container } from "@mui/material"
import Avatar from "@mui/material/Avatar"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import CssBaseline from "@mui/material/CssBaseline"
import Grid from "@mui/material/Grid"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useFormik } from "formik"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import authService from "services/authService"
import type { IChangeUserPassword } from "types/userTypes"
import * as Yup from "yup"

interface IChangePasswordValues {
  password: string
  confirmPassword: string
  firstname: string
  lastname: string
  locale: string
  phonenumber: string
}

export function SingUp() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (authService.resetPasswordSignin === undefined) {
      navigate("/login")
    }
  }, [navigate])

  const initialValues: IChangePasswordValues = {
    password: "",
    confirmPassword: "",
    firstname: "",
    lastname: "",
    locale: "",
    phonenumber: "",
  }

  const changePasswordValidationScheme = Yup.object({
    firstname: Yup.string()
      .required("Please enter your firstname")
      .max(64, "First name can be maximum of 64 characters long"),
    lastname: Yup.string()
      .required("Please enter your lastname")
      .max(64, "Last name can be maximum of 64 characters long"),
    phonenumber: Yup.string()
      .required("Please enter phone number")
      .min(5)
      .matches(
        /^[+][1234567890]+$/,
        "Phone number is not valid. A phone number must start with a plus (+) sign, followed immediately by the country code. A phone number can only contain the + sign and digits.",
      ),
    password: Yup.string()
      .required("Please enter valid password.")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
        "Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character",
      ),
    confirmPassword: Yup.string()
      .required("Required")
      .test("password-match", "Password must match", function (value) {
        return this.parent.password === value
      }),
  })

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: changePasswordValidationScheme,
    onSubmit: (values) => {
      const changeUserPassword: IChangeUserPassword = {
        password: values.password,
        firstname: values.firstname,
        lastname: values.lastname,
        phonenumber: values.phonenumber,
      }

      authService
        .changePassword(changeUserPassword)
        .then((_resp) => {
          navigate("/")
        })
        .catch((err) => {
          console.error(err)
          setError(err.message)
        })
    },
  })

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5" paddingBottom={4}>
          Complete sign up
        </Typography>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                name="confirmPassword"
                label="Confirm password"
                type="password"
                id="confirmPassword"
                autoComplete="new-password"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                autoComplete="given-name"
                name="firstname"
                required
                fullWidth
                id="firstname"
                label="Firstame"
                value={formik.values.firstname}
                onChange={formik.handleChange}
                error={formik.touched.firstname && Boolean(formik.errors.firstname)}
                helperText={formik.touched.firstname && formik.errors.firstname}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="lastname"
                label="Lastname"
                name="lastname"
                value={formik.values.lastname}
                onChange={formik.handleChange}
                error={formik.touched.lastname && Boolean(formik.errors.lastname)}
                helperText={formik.touched.lastname && formik.errors.lastname}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                name="phonenumber"
                label="Phone"
                type="tel"
                id="phonenumber"
                value={formik.values.phonenumber}
                onChange={formik.handleChange}
                error={formik.touched.phonenumber && Boolean(formik.errors.phonenumber)}
                helperText={formik.touched.phonenumber && formik.errors.phonenumber}
              />
            </Grid>
          </Grid>
          {error !== undefined && <Alert severity="error">{error}</Alert>}
          <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
            Send
          </Button>
        </form>
      </Box>
    </Container>
  )
}
